import {usePaymentRequestRepository} from "../../../api/repositories/PaymentRequestRepository";
import * as Const from "./components/filters/PaymentRequestConstants";
import ColouredChip from "../../../component/ColouredChip";
import {PAYMENT_REQUEST_VIEW_DETAIL} from "../../../utils/FinanceScope";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import {IconButton, Tooltip} from "@mui/material";
import {DescriptionRounded} from "@mui/icons-material";
import {
    ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams,
    ITooltipParams
} from "ag-grid-enterprise";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {useMemo, useRef} from "react";
import {FilterDef} from "../../../constants/FinanceModels";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {formatCurrency, formatDate} from "../../../utils/FormatUtils";
import {handleColour, handleStatus} from "./components/filters/PaymentRequestConstants";

export const PaymentRequestsLinked = (props: { paymentRequest: any, hasScope: any }) => {

    const {t} = useTranslation("finance", {keyPrefix: "payments.payment-request"});
    const {getPaymentsRequestsLinked} = usePaymentRequestRepository();

    const gridRef = useRef<AgGridReact>(null);

    const columnDefs: ColDef[] = [
        {
            field: "customerId",
            headerName: t("table.customerId"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) => params.value,
            tooltipValueGetter: (params: ITooltipParams) => params.value
        },
        {
            field: "amountType",
            headerName: t("table.amountType"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) =>
                params.value
                    ? t(
                        Const.convertAmountType(
                            Const.amountTypes,
                            params.value,
                            params.data.usePreAuth
                        )
                    )
                    : "---",
            tooltipValueGetter: (params: ITooltipParams) => params.value
        },
        {
            field: "type",
            headerName: t("table.paymentType"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) => params.value,
            tooltipValueGetter: (params: ITooltipParams) => params.value
        },
        {
            field: "amount",
            headerName: t("table.amount"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) => formatCurrency(params.value, "EUR", 0.01),
            tooltipValueGetter: (params: ITooltipParams) => params.value
        },
        {
            field: "createdDate",
            headerName: t("table.creationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "modifiedDate",
            headerName: t("table.modificationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "lastTransactionDate",
            headerName: t("table.lastTransactionDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "correlationName",
            headerName: t("table.correlationName"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            tooltipValueGetter: (params: ITooltipParams) => params.value,
            cellRenderer: (params: any) => Const.checkCorrelationName(params, t),
        },
        {
            field: "correlationId",
            headerName: t("table.correlationId"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params) => Const.checkCorrelationID(params, t),
        },
        {
            field: "status",
            headerName: t("table.status"),
            minWidth: 150,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                status = handleStatus(params.data);
                let color = handleColour(params.data);
                if (status == "OK") status = "SUCCESS";
                return {
                    component: () => <ColouredChip name={status} color={color}/>,
                };
            },
        },
        {
            field: "_actions",
            headerName: "",
            suppressSizeToFit: true,
            minWidth: 100,
            maxWidth: 110,
            sortable: false,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => {
                        if (!props.hasScope(PAYMENT_REQUEST_VIEW_DETAIL)) {
                            return null;
                        }
                        return (
                            <FinanceDynamicActions>
                                <Tooltip
                                    arrow
                                    placement={"top"}
                                    title={t("table.actions.detail")}
                                >
                                    <IconButton
                                        color="accent"
                                        onClick={() => {
                                            window.open(
                                                `/finance/payments/payment-request/${params.data.id}`,
                                                "_blank"
                                            );
                                        }}
                                    >
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>
                            </FinanceDynamicActions>
                        );
                    },
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content")
        }
    };


    const createDatasource = (filterDefs: FilterDef[], api: GridApi) => {

        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort.toUpperCase();
                getPaymentsRequestsLinked(
                    startRow,
                    endRow - startRow,
                    sort,
                    order,
                    props.paymentRequest.id,
                    props.paymentRequest.parent,
                    props.paymentRequest.correlationName,
                    props.paymentRequest.correlationId,
                    props.paymentRequest.raCode,
                    props.paymentRequest.dealer,
                    props.paymentRequest.legalEntity
                )
                    .then((response) => {
                        let result = response.result;
                        if (result["total-amount"] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result["total-amount"],
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    }


    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    return (
        <div style={{marginTop: '15px'}}>
            <AgGridCustomTable
                rowModelType='serverSide'
                paginationPageSize={10} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                suppressCellFocus
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource([], gridRef.current!.api));
                }}
            >
            </AgGridCustomTable>
        </div>
    );

};